import { disconnectSocket } from './sockets'

/**
 * Ключи localStorage, которые сохраняются при сбросе. Локаль и признак
 * подробного логирования не бывают причиной сбоя, а их потеря затрудняет
 * дальнейшую диагностику.
 *
 * @type {string[]}
 */
export const preservedStorageKeys = [ 'locale', 'debug' ]

/**
 * Предельное время ожидания удаления базы IndexedDB. Удаление блокируется
 * открытыми соединениями других вкладок, поэтому ожидание ограничивается.
 *
 * @type {number}
 */
const databaseRemoveTimeout = 5 * 1000

/**
 * Предельное время выполнения одного шага сброса. Шаг, который браузер
 * не завершает, возвращается как ошибка: страница не должна оставаться
 * в состоянии ожидания.
 *
 * @type {number}
 */
const stepTimeout = 10 * 1000

/**
 * Области сброса.
 *
 * @type {Object<string, string>}
 */
export const resetScopes = {
  all: 'all',
  storage: 'storage',
  cache: 'cache',
  serviceWorker: 'serviceWorker'
}

/**
 * Шаги сброса.
 *
 * @type {Object<string, string>}
 */
export const resetSteps = {
  socket: 'socket',
  localStorage: 'localStorage',
  sessionStorage: 'sessionStorage',
  cookies: 'cookies',
  serviceWorker: 'serviceWorker',
  cacheStorage: 'cacheStorage',
  indexedDB: 'indexedDB'
}

/**
 * Состояния шага сброса. Состояние skipped возвращается, если браузер
 * не поддерживает нужный интерфейс: такой шаг не выполнен, но и не является
 * ошибкой.
 *
 * @type {Object<string, string>}
 */
export const resetStatuses = {
  done: 'done',
  skipped: 'skipped',
  failed: 'failed'
}

/**
 * Результат одного шага сброса.
 *
 * @typedef {Object} ResetReportItem
 * @property {string} step шаг сброса из resetSteps
 * @property {string} status состояние шага из resetStatuses
 * @property {string} [error] текст ошибки, если шаг не выполнен
 */

/**
 * Хранилище localStorage очищается. Ключи из preservedStorageKeys
 * восстанавливаются после очистки.
 *
 * @returns {void}
 */
export const clearLocalStorage = () => {
  const preservedValues = {}
  for (const key of preservedStorageKeys) {
    const value = window.localStorage.getItem(key)
    if (value !== null) {
      preservedValues[key] = value
    }
  }

  window.localStorage.clear()

  for (const key of Object.keys(preservedValues)) {
    window.localStorage.setItem(key, preservedValues[key])
  }
}

/**
 * Хранилище sessionStorage очищается.
 *
 * @returns {void}
 */
export const clearSessionStorage = () => {
  window.sessionStorage.clear()
}

/**
 * Возвращаются пути, по которым выполняется удаление cookies: корень сайта
 * и все вложенные пути текущего адреса. Путь cookie недоступен из JavaScript,
 * поэтому перебираются возможные варианты.
 *
 * @returns {string[]}
 */
const getCookiePaths = () => {
  const paths = [ '/' ]
  const segments = window.location.pathname.split('/').filter(Boolean)

  let currentPath = ''
  for (const segment of segments) {
    currentPath += `/${segment}`
    paths.push(currentPath)
  }

  return paths
}

/**
 * Возвращаются домены, по которым выполняется удаление cookies: текущий хост
 * и все родительские домены до домена второго уровня. Домен cookie недоступен
 * из JavaScript, поэтому перебираются возможные варианты. Для адреса по IP
 * возвращается пустой список: такие cookies удаляются без атрибута domain.
 *
 * @returns {string[]}
 */
const getCookieDomains = () => {
  const hostname = window.location.hostname
  if (hostname.includes(':') || /^[\d.]+$/.test(hostname)) {
    return []
  }

  const segments = hostname.split('.')
  const domains = []
  while (segments.length >= 2) {
    domains.push(segments.join('.'))
    segments.shift()
  }

  return domains
}

/**
 * Cookies удаляются перебором имён из document.cookie. Для каждого имени
 * перебираются возможные пути и домены, так как эти атрибуты недоступны
 * из JavaScript. Cookies с признаком httpOnly из JavaScript удалить нельзя.
 *
 * @returns {void}
 */
export const clearCookies = () => {
  const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  const paths = getCookiePaths()
  const domains = getCookieDomains()

  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0].trim()
    if (!name) {
      continue
    }

    for (const path of paths) {
      document.cookie = `${name}=;${expires};path=${path}`

      for (const domain of domains) {
        document.cookie = `${name}=;${expires};path=${path};domain=${domain}`
        document.cookie = `${name}=;${expires};path=${path};domain=.${domain}`
      }
    }
  }
}

/**
 * Регистрации service worker снимаются. При отсутствии поддержки
 * Service Worker API шаг пропускается. Новый service worker здесь
 * не регистрируется: снятие регистрации не останавливает работающий
 * service worker немедленно, поэтому установка выполняется штатной
 * регистрацией после полной навигации.
 *
 * @returns {Promise<string>}
 */
export const unregisterServiceWorkers = async () => {
  if (typeof navigator.serviceWorker?.getRegistrations !== 'function') {
    return resetStatuses.skipped
  }

  const registrations = await navigator.serviceWorker.getRegistrations()
  const results = await Promise.allSettled(registrations.map(registration => {
    return registration.unregister()
  }))

  const errors = []
  for (const result of results) {
    if (result.status === 'rejected') {
      errors.push(result.reason?.message ?? String(result.reason))
    } else if (result.value === false) {
      errors.push('registration is not unregistered')
    }
  }

  if (errors.length) {
    throw new Error(`service worker: ${errors.length} of ${registrations.length} registrations are not unregistered: ${errors.join('; ')}`)
  }

  return resetStatuses.done
}

/**
 * Хранилище Cache Storage очищается полностью. Удаление всех записей
 * дожидается завершения, чтобы последующая перезагрузка страницы
 * не прервала операцию. При отсутствии поддержки Cache Storage шаг
 * пропускается.
 *
 * @returns {Promise<string>}
 */
export const clearCacheStorage = async () => {
  if (!window.caches) {
    return resetStatuses.skipped
  }

  const names = await window.caches.keys()

  await Promise.all(names.map(name => {
    return window.caches.delete(name)
  }))

  return resetStatuses.done
}

/**
 * База IndexedDB удаляется. Событие blocked не является завершающим:
 * удаление откладывается до закрытия соединений из других вкладок, поэтому
 * ожидание продолжается до события success или до истечения
 * databaseRemoveTimeout. Незавершённое удаление возвращается как ошибка:
 * шаг не должен считаться выполненным, пока база существует.
 *
 * @param {string} name имя базы
 * @returns {Promise<void>}
 */
const removeDatabase = name => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.deleteDatabase(name)
    let blocked = false

    const timeout = setTimeout(() => {
      let reason = 'removal is not completed'
      if (blocked) {
        reason = 'removal is blocked by another connection'
      }

      reject(new Error(`IndexedDB database "${name}": ${reason}`))
    }, databaseRemoveTimeout)

    request.onsuccess = () => {
      clearTimeout(timeout)
      resolve()
    }
    request.onblocked = () => {
      blocked = true
    }
    request.onerror = () => {
      clearTimeout(timeout)
      reject(request.error)
    }
  })
}
/**
 * Базы IndexedDB удаляются. Перечисление баз поддерживается не всеми
 * браузерами, при отсутствии поддержки шаг пропускается.
 *
 * @returns {Promise<string>}
 */
export const clearIndexedDB = async () => {
  if (typeof window.indexedDB?.databases !== 'function') {
    return resetStatuses.skipped
  }

  const databases = await window.indexedDB.databases()
  const names = databases.map(database => {
    return database.name
  }).filter(Boolean)

  await Promise.all(names.map(name => {
    return removeDatabase(name)
  }))

  return resetStatuses.done
}

/**
 * Обработчики шагов сброса.
 *
 * @type {Object<string, Function>}
 */
const resetHandlers = {
  [resetSteps.socket]: disconnectSocket,
  [resetSteps.localStorage]: clearLocalStorage,
  [resetSteps.sessionStorage]: clearSessionStorage,
  [resetSteps.cookies]: clearCookies,
  [resetSteps.serviceWorker]: unregisterServiceWorkers,
  [resetSteps.cacheStorage]: clearCacheStorage,
  [resetSteps.indexedDB]: clearIndexedDB
}

/**
 * Состав шагов по областям сброса. Соединение разрывается первым, чтобы
 * после очистки токена не осталось активного соединения со старыми данными.
 * Регистрация service worker снимается до очистки Cache Storage, иначе
 * работающий service worker создаёт кеши заново.
 *
 * @type {Object<string, string[]>}
 */
const resetScopeSteps = {
  [resetScopes.all]: [
    resetSteps.socket,
    resetSteps.localStorage,
    resetSteps.sessionStorage,
    resetSteps.cookies,
    resetSteps.serviceWorker,
    resetSteps.cacheStorage,
    resetSteps.indexedDB
  ],
  [resetScopes.storage]: [
    resetSteps.socket,
    resetSteps.localStorage,
    resetSteps.sessionStorage,
    resetSteps.cookies
  ],
  [resetScopes.cache]: [
    resetSteps.cacheStorage,
    resetSteps.indexedDB
  ],
  [resetScopes.serviceWorker]: [
    resetSteps.serviceWorker,
    resetSteps.cacheStorage
  ]
}

/**
 * Шаг выполняется с ограничением по времени. Незавершённый шаг возвращается
 * как ошибка: иначе отчёт о сбросе не будет получен никогда.
 *
 * @param {string} step шаг сброса из resetSteps
 * @param {Function} handler обработчик шага
 * @returns {Promise<string|undefined>}
 */
const runStep = (step, handler) => {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`step "${step}": is not completed in ${stepTimeout} ms`))
    }, stepTimeout)

    const complete = result => {
      clearTimeout(timeout)
      resolve(result)
    }
    const fail = error => {
      clearTimeout(timeout)
      reject(error)
    }

    Promise.resolve().then(handler).then(complete, fail)
  })
}

/**
 * Выполняются шаги указанной области сброса. Каждый шаг выполняется
 * независимо: ошибка одного шага не прерывает остальные. Возвращается отчёт,
 * по которому вызывающий код решает, можно ли перезагружать страницу.
 *
 * @param {string} scope область сброса из resetScopes
 * @returns {Promise<ResetReportItem[]>}
 */
export const resetClientState = async scope => {
  const steps = resetScopeSteps[scope] ?? resetScopeSteps[resetScopes.all]
  const report = []

  for (const step of steps) {
    try {
      const result = await runStep(step, resetHandlers[step])

      let status = resetStatuses.done
      if (result === resetStatuses.skipped) {
        status = resetStatuses.skipped
      }

      report.push({
        step,
        status
      })
    } catch (error) {
      report.push({
        step,
        status: resetStatuses.failed,
        error: error?.message ?? String(error)
      })
    }
  }

  return report
}
